// YtreCustomNudgeModal.jsx — Iter160c (Randy pt-ytre-p1).
//
// User-scoped custom nudge builder for the YTRE Silo-response dropdown.
// Structurally mirrors the SuperAdmin [YTRE — Global Commands] editor,
// but POSTs to /api/commands (scope=ytre) instead of /api/admin/commands
// so each end user builds their own private library.
//
// The nudge's `instructions` field becomes a RESPONSE-STYLE directive on
// the silo's (tier 1,2,3, RAG/vector, pubQA) pipeline — meaning the silo
// STILL uses its full retrieval stack, but the STANCE of its answer is
// shaped by whatever instruction the user drafts here.
//
// The right-hand side embeds AiBuddy in compact mode with YTRE-tuned
// title/subtitle/emptyHint so it can help the user brainstorm a nudge
// instruction and paste the result directly into the Instructions field.

import { useState } from "react";
import { toast } from "sonner";
import { X, FloppyDisk, MagicWand } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import AiBuddy from "@/components/AiBuddy";

export default function YtreCustomNudgeModal({ siloId = null, editing = null, onClose, onCreated }) {
  const [name, setName] = useState(editing?.name || "");
  const [instructions, setInstructions] = useState(editing?.instructions || "");
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing?.id;

  const save = async () => {
    if (!name.trim() || !instructions.trim()) return toast.error("Name and instructions required.");
    if (name.length > 80) return toast.error("Name must be 80 characters or less.");
    if (instructions.length > 4000) return toast.error("Instruction must be 4000 characters or less.");
    setSaving(true);
    try {
      // Jun 2026 (Randy) — "when making a custom operation in the YTRE, it
      // allows me to delete it... but I dont see the option to edit it."
      // Same modal, same fields; PATCH when it already exists.
      const r = isEdit
        ? await api.patch(`/commands/${editing.id}`, {
            name: name.trim(),
            instructions: instructions.trim(),
          })
        : await api.post("/commands", {
            name: name.trim(),
            instructions: instructions.trim(),
            scope: "ytre",
          });
      toast.success(isEdit ? "Custom nudge updated." : "Custom nudge saved.");
      onCreated && onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[240] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
      data-testid="ytre-custom-nudge-modal"
    >
      <div
        className="bg-[#FDFCF8] rounded-2xl w-full max-w-3xl max-h-[92dvh] flex flex-col shadow-2xl border hairline overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b hairline shrink-0 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">Custom nudge</div>
            <h2 className="font-serif-display text-lg text-[#1C1B1A] flex items-center gap-2 truncate">
              <MagicWand size={16} weight="duotone" className="text-[#4A5D4E]" />
              {isEdit ? "Edit your YTRE nudge" : "Design a private YTRE nudge"}
            </h2>
          </div>
          <button onClick={onClose} data-testid="ytre-custom-nudge-close" className="p-1 rounded hover:bg-[#F0F1EE]">
            <X size={18} />
          </button>
        </div>

        {/* Body — left: form, right: AiBuddy */}
        <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-4 px-5 py-4">
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1">Nudge name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder='e.g. "Servo-slop pushback"'
                maxLength={80}
                data-testid="ytre-custom-nudge-name"
                className="w-full text-[13px] px-3 py-2 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E]"
              />
              <div className="text-[10px] text-[#6B6A68] italic mt-1">Appears in your Ask-silo dropdown alongside the global nudges.</div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1">Instruction (the stance)</label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Describe the STANCE you want the silo to take when responding to a transcript slice. Example: 'Treat the segment as an overconfident beginner-tier claim and respond as a patient master craftsman who names what's missing without ridiculing.'"
                rows={12}
                maxLength={4000}
                data-testid="ytre-custom-nudge-instructions"
                className="w-full text-[12.5px] px-3 py-2 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E] resize-y leading-relaxed"
              />
              <div className="text-[10px] text-[#6B6A68] italic mt-1">
                This instruction wraps the silo&apos;s answer style — the silo still uses its full library (tier 1/2/3), tuning, and public-QA logic.
              </div>
              <div className="text-[10px] text-[#6B6A68] mt-1 text-right">{instructions.length} / 4000</div>
            </div>
          </div>

          {/* AiBuddy — same compact drawer Workbench + SuperAdmin use.
              Title / hint tuned so it helps the user draft a STANCE
              (not a workbench operation). */}
          <div className="border hairline rounded-lg p-2 bg-white/60" data-testid="ytre-custom-nudge-aibuddy-mount">
            <AiBuddy
              surface={`ytre-custom-nudge${siloId ? `-${siloId}` : ""}`}
              compact
              title="YTRE — AI Nudge Assist"
              defaultOpen
              subtitle="Turns what you say into the nudge instruction"
              emptyHint="Say what you want the silo to DO about a segment — clarify the debatable points, push back on bad advice, agree and expand, whatever it is — and I'll write that out as the instruction itself. I never need the transcript: the nudge is written now and runs later."
              inputPlaceholder="I want the silo to…"
              onPasteToInstructions={(text) => {
                setInstructions(text);
                toast.success("Pasted into the instruction field.");
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t hairline shrink-0 flex items-center justify-end gap-2 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
          <button onClick={onClose} className="text-[12px] px-3 py-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !name.trim() || !instructions.trim()}
            data-testid="ytre-custom-nudge-save"
            className="text-[12.5px] px-4 py-2 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-40 flex items-center gap-1.5 font-medium"
          >
            <FloppyDisk size={12} weight="bold" />
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Save nudge")}
          </button>
        </div>
      </div>
    </div>
  );
}
